import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { usePowerMonitorAPI } from '../../api/powermonitor/usePowerMonitorAPI';
import { KPIService } from '../../api/kpi/KPIService';
import SensorCard from '../PowerMonitor/components/SensorCard';
import KpiChart from '../../components/Chart/KpiChart';
import { FaTachometerAlt, FaBolt } from 'react-icons/fa';
import { getKpiFieldDisplay } from '../../utils/kpiFieldIcon';
import './Dashboard.css';

// 트랙 9-1: KPI 외부 데이터 기간 default = 30일 롤링 (오늘-30일 ~ 오늘)
// 매월 1~2일에도 충분한 추이가 보이도록 월 단위 → 30일 롤링으로 전환.

const SENSOR_INTERVAL_MS = 30000;
const SENSOR_RANGE_MINUTES = 10;

const Dashboard = () => {
  const session = useSelector((state) => state.session.data);
  const api = usePowerMonitorAPI();

  const [sensors, setSensors] = useState([]);
  const [sensorLoading, setSensorLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  const [kpiLoading, setKpiLoading] = useState(true);

  // 업체별 외부 KPI 지표
  const [indicators, setIndicators] = useState([]);
  const [externalKpiData, setExternalKpiData] = useState({});

  // 30일 롤링 기간 (오늘-30일 ~ 오늘). 마운트 시 1회 계산.
  const dateRange = useMemo(() => {
    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 30);
    return { start: fmt(startDate), end: fmt(today) };
  }, []);

  const fetchSensors = useCallback(async () => {
    if (!session?.companySeq) return;
    setSensorLoading(true);
    const data = await api.powerMonitor.get({
      companySeq: session.companySeq,
      rangeMinutes: SENSOR_RANGE_MINUTES,
    });
    if (data) {
      setSensors(data);
      setLastUpdated(new Date());
    }
    setSensorLoading(false);
  }, [session?.companySeq]);

  useEffect(() => {
    fetchSensors();
    intervalRef.current = setInterval(fetchSensors, SENSOR_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [fetchSensors]);

  // 업체 KPI 지표 로드
  useEffect(() => {
    if (!session?.companySeq) return;
    KPIService.indicators({ companySeq: session.companySeq })
      .then(data => { if (data) setIndicators(data); })
      .catch(() => {});
  }, [session?.companySeq]);

  // 외부 KPI 데이터 로드 (지표 기준)
  useEffect(() => {
    if (!session?.companySeq || indicators.length === 0) {
      setKpiLoading(false);
      return;
    }
    const params = {
      companySeq: session.companySeq,
      companyName: session.companyName,
      start: dateRange.start,
      end: dateRange.end,
    };

    setKpiLoading(true);

    const promises = indicators.map(ind =>
      KPIService.TimeSeries.externalData({
        ...params,
        fldCd: ind.kpiFldCd,
        dtlCd: ind.kpiDtlCd,
      }).then(data => ({ key: `${ind.kpiFldCd}_${ind.kpiDtlCd}`, data: data || [] }))
        .catch(() => ({ key: `${ind.kpiFldCd}_${ind.kpiDtlCd}`, data: [] }))
    );

    Promise.all(promises).then((results) => {
      const extMap = {};
      results.forEach(r => { extMap[r.key] = r.data; });
      setExternalKpiData(extMap);
      setKpiLoading(false);
    });
  }, [session?.companySeq, dateRange.start, dateRange.end, indicators]);

  // 외부 KPI별 평균값 계산
  const getExternalAvg = (key) => {
    const data = externalKpiData[key] || [];
    if (data.length === 0) return null;
    const nums = data.map(d => parseFloat(d.numeric || d.value || '0')).filter(n => !isNaN(n));
    if (nums.length === 0) return null;
    return (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1);
  };

  // 외부 KPI별 차트 데이터
  const getExternalChartData = (key) => {
    const data = externalKpiData[key] || [];
    return data.map(d => ({
      name: String(d.name).length > 5 ? String(d.name).slice(5) : String(d.name),
      value: parseFloat(d.numeric || d.value || '0'),
    }));
  };

  const periodLabel = `최근 30일 (${dateRange.start} ~ ${dateRange.end})`;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h3><FaTachometerAlt style={{ marginRight: 8 }} />대시보드</h3>
          {lastUpdated && (
            <span className="dashboard-updated">
              센서 업데이트: {lastUpdated.toLocaleTimeString('ko-KR')}
            </span>
          )}
        </div>
        <div className="dashboard-period-label" title="KPI 외부 데이터 기간">
          {periodLabel}
        </div>
      </div>

      {/* KPI 요약 카드 — 측정 지표만 표시 */}
      <div className="dashboard-section">
        <h4 className="section-title">최근 30일 KPI 요약</h4>
        {indicators.length > 0 ? (
          <div className="kpi-summary-row">
            {indicators.map(ind => {
              const key = `${ind.kpiFldCd}_${ind.kpiDtlCd}`;
              const avg = getExternalAvg(key);
              const { Icon, color } = getKpiFieldDisplay(ind.kpiFldCd);
              return (
                <div className="kpi-stat-card" key={key}>
                  <div className="kpi-stat-icon" style={{ color }}>
                    <Icon size={24} />
                  </div>
                  <div className="kpi-stat-body">
                    <span className="kpi-stat-label">{ind.indicatorName}</span>
                    <span className="kpi-stat-value">
                      {kpiLoading ? '...' : avg ? `${avg}${ind.unit || ''}` : '-'}
                    </span>
                    {ind.targetValue != null && (
                      <span className="kpi-stat-target">
                        목표: {ind.targetValue}{ind.unit || ''}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-data-placeholder">
            {kpiLoading ? '로딩 중...' : '등록된 KPI 지표가 없습니다.'}
          </div>
        )}
      </div>

      {/* 실시간 가동 모니터링 */}
      <div className="dashboard-section">
        <h4 className="section-title">
          <FaBolt style={{ marginRight: 6 }} />
          실시간 가동 모니터링
          {sensorLoading && <span className="loading-indicator">갱신 중...</span>}
        </h4>
        {sensors.length > 0 ? (
          <div className="dashboard-sensor-grid">
            {sensors.map(sensor => (
              <SensorCard key={sensor.sensorSeq} sensor={sensor} />
            ))}
          </div>
        ) : (
          <div className="no-data-placeholder">
            {sensorLoading ? '데이터를 불러오는 중...' : '등록된 센서가 없습니다.'}
          </div>
        )}
      </div>

      {/* 최근 30일 추이 — 측정 지표만 표시 */}
      {indicators.length > 0 && (
        <div className="dashboard-section">
          <h4 className="section-title">최근 30일 추이</h4>
          <div className="chart-row">
            {indicators.map(ind => {
              const key = `${ind.kpiFldCd}_${ind.kpiDtlCd}`;
              const chartData = getExternalChartData(key);
              return (
                <div className="chart-panel" key={key}>
                  <h5 className="chart-panel-title">일별 {ind.indicatorName} 추이</h5>
                  <div className="chart-container">
                    {kpiLoading ? (
                      <div className="no-data-placeholder">로딩 중...</div>
                    ) : chartData.length > 0 ? (
                      <KpiChart label={`${ind.indicatorName} (${ind.unit || ''})`} value={chartData} />
                    ) : (
                      <div className="no-data-placeholder">데이터 없음</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
